import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react'
import {
  MessageBar,
  MessageBarType,
  Stack,
  Text,
  DefaultButton,
} from '@fluentui/react'
import {
  ComposedChart,
  Bar,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  LabelList,
} from 'recharts'
import { apiService } from '../../services/apiService'
import { useApi } from '../../hooks/useApi'
import { useDashboardPrefs, type DashboardChartType } from '../../context/DashboardPrefsContext'
import { useDashboardTokens, CHANGE_CATEGORIES, type DashboardTokens } from '../../theme/dashboardTokens'
import { ChartControls } from './ChartControls'
import { isWeekend } from '../../utils/date'
import type { ApplicationChangesData } from '../../types'

/**
 * Lifecycle Command Center chart (v2, Lane A / V5).
 *
 * Evolves the v1 seed `LifecycleHistogram.tsx` into the polished, preference-
 * driven, WCAG 2.1 AA chart engine for the redesigned Dashboard:
 *   - 5 chart treatments (diverging default / area / grouped / weekly / heatmap),
 *     switched by `useDashboardPrefs().chartType`.
 *   - Range-driven AGGREGATION — 7d daily+labels · 30d daily · 90d auto-weekly
 *     (the v1 mock's `slice(-15)` bug made 7/30/90 identical; here the range
 *     drives bucketing, not just the x-domain).
 *   - Colors come exclusively from `useDashboardTokens()` (no hard-coded hex),
 *     so both the dark primary and its light twin track the theme flip.
 *   - `showValues` overlays per-category counts (LabelList); weekly is always
 *     labelled.
 *   - a11y: `role="img"` + range-aware `aria-label` + visually-hidden long
 *     description; a "View as table" toggle rendering a real `<table>` (1.1.1);
 *     keyboard day-selection (focusable region, arrow keys move a marked day,
 *     Enter/Space → `onDayClick`); non-color cues (diverging up/down position +
 *     direct labels, not green/red alone); a solid zero-axis via `--dv-axis`.
 *
 * Standalone: it wires nothing into `Dashboard.tsx` — that's Lane B (V7+). The
 * only external contract is the `onDayClick(date)` callback (kept from v1) that
 * Lane B's routed drill hooks into.
 */

/** One of the four lifecycle change category keys (order/naming from CHANGE_CATEGORIES). */
type CatKey = (typeof CHANGE_CATEGORIES)[number]['key']

interface NormalizedBucket {
  /** Representative date passed to onDayClick (day for daily, week-start for weekly). */
  date: string
  /** Axis / table label. */
  label: string
  added: number
  changed: number
  cred: number
  removed: number
  /** -removed, for the diverging treatment (removals below the zero axis). */
  removedNeg: number
  /** Running total of managed apps as of the bucket's last day. */
  cumulativeTotal: number
  /** added + changed + cred + removed — heatmap intensity + table total. */
  total: number
  isWeekend: boolean
}

interface LifecycleChartProps {
  /** Initial range in days (default 30). The component owns the selector after mount. */
  initialDays?: number
  height?: number
  /** Called with the clicked/selected day's date (YYYY-MM-DD). Wired to the drill (Lane B). */
  onDayClick?: (date: string) => void
  /**
   * Called when the user changes the range. The chart still owns the selector —
   * this only lets the page mirror the range so the KPI band reads the same
   * window as the chart (the band's deltas are meaningless against a different one).
   */
  onDaysChange?: (days: number) => void
}

const SR_ONLY: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
}

function weekdayIndex(isoDate: string): number {
  const [y, m, d] = isoDate.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay()
}

function shortDate(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function catColor(tokens: DashboardTokens, key: CatKey): string {
  const cat = CHANGE_CATEGORIES.find((c) => c.key === key)!
  return tokens[cat.token] || '#888'
}

export function LifecycleChart({
  initialDays = 30,
  height = 340,
  onDayClick,
  onDaysChange,
}: LifecycleChartProps) {
  const [days, setDays] = useState<number>(initialDays)
  const [viewAsTable, setViewAsTable] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)

  const handleDaysChange = useCallback(
    (d: number) => {
      setDays(d)
      onDaysChange?.(d)
    },
    [onDaysChange],
  )

  const { chartType, showValues } = useDashboardPrefs()
  const tokens = useDashboardTokens()

  const fetchChanges = useCallback(() => apiService.getApplicationChanges(days), [days])
  const { data, loading, error } = useApi<ApplicationChangesData>(fetchChanges, [days])

  const dailyPoints = data?.dataPoints ?? []

  // Range drives granularity: 90d (or the weekly treatment) buckets by week; the
  // heatmap always needs the raw daily grid. Everything else is daily.
  const weekly = chartType === 'weekly' || days >= 90
  const granularity: 'daily' | 'weekly' = chartType === 'heatmap' ? 'daily' : weekly ? 'weekly' : 'daily'

  const buckets = useMemo<NormalizedBucket[]>(() => {
    const daily: NormalizedBucket[] = dailyPoints.map((p) => {
      const removed = p.removed ?? 0
      const added = p.added ?? 0
      const changed = p.configChanged ?? 0
      const cred = p.credentialChanges ?? 0
      return {
        date: p.date,
        label: shortDate(p.date),
        added,
        changed,
        cred,
        removed,
        removedNeg: -removed,
        cumulativeTotal: p.cumulativeTotal ?? 0,
        total: added + changed + cred + removed,
        isWeekend: isWeekend(p.date),
      }
    })

    if (granularity === 'daily') return daily

    // Weekly: chunk the ordered daily points into groups of 7.
    const out: NormalizedBucket[] = []
    for (let i = 0; i < daily.length; i += 7) {
      const chunk = daily.slice(i, i + 7)
      if (!chunk.length) continue
      const sum = (sel: (b: NormalizedBucket) => number) => chunk.reduce((a, b) => a + sel(b), 0)
      const added = sum((b) => b.added)
      const changed = sum((b) => b.changed)
      const cred = sum((b) => b.cred)
      const removed = sum((b) => b.removed)
      out.push({
        date: chunk[0].date, // week start — the drill treats it as the bucket anchor
        label: `Wk ${shortDate(chunk[0].date)}`,
        added,
        changed,
        cred,
        removed,
        removedNeg: -removed,
        cumulativeTotal: chunk[chunk.length - 1].cumulativeTotal,
        total: added + changed + cred + removed,
        isWeekend: false, // weekend shading is a daily-only affordance
      })
    }
    return out
  }, [dailyPoints, granularity])

  const hasPoints = buckets.length > 0

  /**
   * The drill only makes sense when a bucket IS a day. `getChangeDetail` returns
   * one day's diff, so drilling from a weekly bucket would open the week's first
   * day while the bar reports the whole week's total — a bar reading "14 changes"
   * opening a day showing 2. Rather than mislead, weekly buckets aren't clickable;
   * the hint below tells the user how to get the drill back.
   */
  const canDrill = granularity === 'daily' && !!onDayClick
  const drillTo = useCallback(
    (date: string) => {
      if (canDrill) onDayClick?.(date)
    },
    [canDrill, onDayClick],
  )

  /** Data came back, but the estate genuinely didn't move — distinct from "no data yet". */
  const quietPeriod = hasPoints && buckets.every((b) => b.total === 0)

  // Clamp / reset the keyboard cursor whenever the bucket set changes.
  useEffect(() => {
    setFocusedIndex((i) => (i >= buckets.length ? buckets.length - 1 : i))
  }, [buckets.length])

  // Contiguous weekend runs → one shaded band each (daily granularity only).
  const weekendBands = useMemo(() => {
    if (granularity !== 'daily') return []
    const bands: Array<{ x1: string; x2: string }> = []
    let run: string[] = []
    const flush = () => {
      if (run.length) bands.push({ x1: run[0], x2: run[run.length - 1] })
      run = []
    }
    for (const b of buckets) {
      if (b.isWeekend) run.push(b.label)
      else flush()
    }
    flush()
    return bands
  }, [buckets, granularity])

  // Range-aware summary used for the chart's accessible name + the caption.
  const summaryText = useMemo(() => {
    if (!data) return ''
    const s = data.summary
    const g = granularity === 'weekly' ? ' (aggregated weekly)' : ''
    return (
      `Change activity over the last ${days} days${g}: ` +
      `${s.totalAdded} added, ${s.totalConfigChanged} changed, ` +
      `${s.totalCredentialChanges} credential events, ${s.totalRemoved} removed. ` +
      `Currently ${s.currentTotal} managed applications.`
    )
  }, [data, days, granularity])

  const selected = focusedIndex >= 0 && focusedIndex < buckets.length ? buckets[focusedIndex] : null
  const liveText = selected
    ? `${selected.label}: ${selected.added} added, ${selected.changed} changed, ` +
      `${selected.cred} credential, ${selected.removed} removed.`
    : ''

  const moveFocus = useCallback(
    (next: number) => {
      if (!buckets.length) return
      const clamped = Math.max(0, Math.min(buckets.length - 1, next))
      setFocusedIndex(clamped)
    },
    [buckets.length],
  )

  const handleChartKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (!buckets.length) return
      const cur = focusedIndex < 0 ? buckets.length - 1 : focusedIndex
      // Month-calendar layout: weekday columns, one row per week. →/← step a day
      // along the row, ↑/↓ jump a whole week. Linear charts only use →/←.
      const verticalStep = chartType === 'heatmap' ? 7 : 1
      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault()
          moveFocus(cur + 1)
          break
        case 'ArrowLeft':
          e.preventDefault()
          moveFocus(cur - 1)
          break
        case 'ArrowDown':
          e.preventDefault()
          moveFocus(cur + verticalStep)
          break
        case 'ArrowUp':
          e.preventDefault()
          moveFocus(cur - verticalStep)
          break
        case 'Home':
          e.preventDefault()
          moveFocus(0)
          break
        case 'End':
          e.preventDefault()
          moveFocus(buckets.length - 1)
          break
        case 'Enter':
        case ' ':
          e.preventDefault()
          if (cur >= 0) drillTo(buckets[cur].date)
          break
        default:
          break
      }
    },
    [buckets, focusedIndex, chartType, moveFocus, drillTo],
  )

  const onChartFocus = useCallback(() => {
    if (focusedIndex < 0 && buckets.length) setFocusedIndex(buckets.length - 1)
  }, [focusedIndex, buckets.length])

  return (
    <div>
      <Stack
        horizontal
        horizontalAlign="space-between"
        verticalAlign="center"
        wrap
        tokens={{ childrenGap: 12 }}
        styles={{ root: { marginBottom: 12 } }}
      >
        <Text
          variant="mediumPlus"
          styles={{ root: { fontWeight: 600, color: 'var(--dv-text)' } }}
        >
          Change activity{granularity === 'weekly' ? ' · weekly' : ''}
        </Text>
        <ChartControls
          days={days}
          onDaysChange={handleDaysChange}
          viewAsTable={viewAsTable}
          onToggleTable={() => setViewAsTable((v) => !v)}
        />
      </Stack>

      {loading ? (
        <ChartSkeleton height={height} />
      ) : error ? (
        <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
      ) : !hasPoints ? (
        <Stack horizontalAlign="center" styles={{ root: { padding: 40 } }}>
          <Text variant="medium" styles={{ root: { color: 'var(--dv-muted)' } }}>
            No change history for the last {days} days yet — the daily diff needs two consecutive
            snapshots (or seed demo data via the synthetic change-history backfill).
          </Text>
        </Stack>
      ) : quietPeriod ? (
        <Stack horizontalAlign="center" tokens={{ childrenGap: 4 }} styles={{ root: { padding: 40 } }}>
          <Text variant="mediumPlus" styles={{ root: { fontWeight: 600, color: 'var(--dv-text)' } }}>
            No changes in the last {days} days
          </Text>
          <Text variant="medium" styles={{ root: { color: 'var(--dv-muted)' } }}>
            The estate was quiet — snapshots ran, nothing moved.
          </Text>
        </Stack>
      ) : viewAsTable ? (
        <ChangeTable buckets={buckets} caption={summaryText} onDayClick={canDrill ? drillTo : undefined} />
      ) : (
        <>
          {/* Accessible interactive region: keyboard day-selection + a live announcer.
              The SVG marks are decorative to AT (role=img + aria-label carry the meaning). */}
          <div
            role="img"
            aria-label={summaryText}
            tabIndex={0}
            onKeyDown={handleChartKeyDown}
            onFocus={onChartFocus}
            style={{ position: 'relative', outlineOffset: 3 }}
          >
            <span style={SR_ONLY}>
              {summaryText}{' '}
              {canDrill
                ? 'Use the left and right arrow keys to select a day, then press Enter to open its detail.'
                : 'Use the left and right arrow keys to select a week. Buckets are aggregated weekly, so day detail is unavailable at this range.'}
            </span>
            {chartType === 'heatmap' ? (
              <Heatmap
                buckets={buckets}
                tokens={tokens}
                showValues={showValues}
                focusedIndex={focusedIndex}
                onDayClick={canDrill ? drillTo : undefined}
              />
            ) : (
              <ChartBody
                chartType={chartType}
                buckets={buckets}
                tokens={tokens}
                height={height}
                showValues={showValues || granularity === 'weekly'}
                weekendBands={weekendBands}
                focusedLabel={selected?.label}
                onIndexClick={(idx) => {
                  setFocusedIndex(idx)
                  if (idx >= 0) drillTo(buckets[idx].date)
                }}
              />
            )}
          </div>
          <div aria-live="polite" style={SR_ONLY}>
            {liveText}
          </div>

          {/* Say why the drill is gone, rather than leaving dead-feeling bars. */}
          {!canDrill && onDayClick ? (
            <Text
              variant="small"
              styles={{ root: { display: 'block', marginTop: 8, color: 'var(--dv-muted)' } }}
            >
              Bars are weekly totals at this range — pick 7 or 30 days (or a non-weekly chart type) to
              open a single day&apos;s detail.
            </Text>
          ) : null}
        </>
      )}
    </div>
  )
}

/**
 * Loading skeleton (V11). Mirrors the chart's real footprint — a row of bars over
 * an axis — so the card doesn't collapse and then jolt when data lands.
 */
function ChartSkeleton({ height }: { height: number }) {
  const bars = Array.from({ length: 24 }, (_, i) => i)
  // Deterministic pseudo-random heights: a fixed shape reads as "loading", but a
  // flat one reads as "empty".
  const heightFor = (i: number) => 25 + ((i * 37) % 60)
  return (
    <div aria-hidden style={{ height, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: height - 24 }}>
        {bars.map((i) => (
          <div
            key={i}
            className="dv-skeleton"
            style={{ flex: 1, height: `${heightFor(i)}%`, borderRadius: '3px 3px 0 0' }}
          />
        ))}
      </div>
      <div className="dv-skeleton" style={{ height: 2, marginTop: 8, borderRadius: 1 }} />
      <span style={SR_ONLY} aria-live="polite">
        Loading change activity…
      </span>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Recharts body (diverging / area / grouped / weekly)
// ─────────────────────────────────────────────────────────────────────────────

interface ChartBodyProps {
  chartType: Exclude<DashboardChartType, 'heatmap'>
  buckets: NormalizedBucket[]
  tokens: DashboardTokens
  height: number
  showValues: boolean
  weekendBands: Array<{ x1: string; x2: string }>
  focusedLabel?: string
  onIndexClick: (index: number) => void
}

function ChartBody({
  chartType,
  buckets,
  tokens,
  height,
  showValues,
  weekendBands,
  focusedLabel,
  onIndexClick,
}: ChartBodyProps) {
  const axis = tokens.axis || '#64748b'
  const grid = tokens.line || '#e6e9f0'
  const tickFill = tokens.muted || '#64748b'
  const totalStroke = tokens.text || '#0b1220'

  const labelStyle = { fontSize: 10, fill: tokens.slate || tickFill } as const

  const common: ReactNode[] = [
    <CartesianGrid key="grid" strokeDasharray="3 3" stroke={grid} vertical={false} />,
    ...weekendBands.map((b) => (
      <ReferenceArea
        key={`wk-${b.x1}`}
        yAxisId="left"
        x1={b.x1}
        x2={b.x2}
        fill={axis}
        fillOpacity={0.06}
        ifOverflow="extendDomain"
      />
    )),
    <XAxis key="x" dataKey="label" tick={{ fontSize: 12, fill: tickFill }} interval="preserveStartEnd" />,
    <YAxis
      key="yl"
      yAxisId="left"
      tick={{ fontSize: 12, fill: tickFill }}
      allowDecimals={false}
      label={{ value: 'Daily changes', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: tickFill } }}
    />,
    <YAxis
      key="yr"
      yAxisId="right"
      orientation="right"
      tick={{ fontSize: 12, fill: tickFill }}
      allowDecimals={false}
      label={{ value: 'Total apps', angle: 90, position: 'insideRight', style: { fontSize: 12, fill: tickFill } }}
    />,
    <Tooltip
      key="tt"
      formatter={((value: number | undefined, name: string) => [
        name === 'Removed' ? Math.abs(Number(value ?? 0)) : (value ?? 0),
        name,
      ]) as never}
      contentStyle={{
        backgroundColor: tokens.elevated || '#fff',
        border: `1px solid ${grid}`,
        borderRadius: 6,
        color: tokens.text,
      }}
      labelStyle={{ color: tokens.text }}
      itemStyle={{ color: tokens.text }}
    />,
    <Legend key="lg" />,
  ]

  // Solid zero-axis (diverging reads growth above / churn below it).
  if (chartType === 'diverging') {
    common.push(<ReferenceLine key="zero" yAxisId="left" y={0} stroke={axis} strokeWidth={1.5} />)
  }

  // Focused-day marker for keyboard selection.
  if (focusedLabel) {
    common.push(
      <ReferenceLine
        key="focus"
        yAxisId="left"
        x={focusedLabel}
        stroke={tokens.focus || '#4f46e5'}
        strokeWidth={2}
        strokeDasharray="4 2"
      />,
    )
  }

  const marks = buildMarks(chartType, tokens, showValues, totalStroke, labelStyle)

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart
        data={buckets}
        margin={{ top: 12, right: 30, left: 0, bottom: 0 }}
        onClick={(state) => {
          const idx = (state as { activeTooltipIndex?: number | null })?.activeTooltipIndex
          if (typeof idx === 'number' && idx >= 0) onIndexClick(idx)
        }}
        stackOffset="sign"
      >
        {common}
        {marks}
      </ComposedChart>
    </ResponsiveContainer>
  )
}

function buildMarks(
  chartType: Exclude<DashboardChartType, 'heatmap'>,
  tokens: DashboardTokens,
  showValues: boolean,
  totalStroke: string,
  labelStyle: { fontSize: number; fill: string },
): ReactNode[] {
  const cAdded = catColor(tokens, 'added')
  const cChanged = catColor(tokens, 'changed')
  const cCred = catColor(tokens, 'cred')
  const cRemoved = catColor(tokens, 'removed')

  const valueLabel = (dataKey: string, position: 'top' | 'bottom' = 'top') =>
    showValues ? (
      <LabelList
        dataKey={dataKey}
        position={position}
        style={labelStyle}
        formatter={((v: number | undefined) => (v ? Math.abs(v) : '')) as never}
      />
    ) : null

  const totalLine = (
    <Line
      key="total"
      yAxisId="right"
      type="monotone"
      dataKey="cumulativeTotal"
      name="Total apps"
      stroke={totalStroke}
      strokeWidth={2}
      dot={false}
      activeDot={{ r: 5 }}
    />
  )

  if (chartType === 'diverging') {
    // Adds / changes / creds stack UP; removals stack DOWN (negative field).
    return [
      <Bar key="added" yAxisId="left" dataKey="added" name="Added" fill={cAdded} stackId="pos">
        {valueLabel('added')}
      </Bar>,
      <Bar key="changed" yAxisId="left" dataKey="changed" name="Changed" fill={cChanged} stackId="pos">
        {valueLabel('changed')}
      </Bar>,
      <Bar key="cred" yAxisId="left" dataKey="cred" name="Credential" fill={cCred} stackId="pos">
        {valueLabel('cred')}
      </Bar>,
      <Bar key="removed" yAxisId="left" dataKey="removedNeg" name="Removed" fill={cRemoved} stackId="neg">
        {valueLabel('removedNeg', 'bottom')}
      </Bar>,
      totalLine,
    ]
  }

  if (chartType === 'area') {
    // Translucent, non-additive (each area from zero) so overlaps read as layers.
    const area = (key: CatKey, dataKey: string, name: string, color: string) => (
      <Area
        key={key}
        yAxisId="left"
        type="monotone"
        dataKey={dataKey}
        name={name}
        stroke={color}
        fill={color}
        fillOpacity={0.28}
        strokeWidth={2}
        activeDot={{ r: 4 }}
      >
        {valueLabel(dataKey)}
      </Area>
    )
    return [
      area('added', 'added', 'Added', cAdded),
      area('changed', 'changed', 'Changed', cChanged),
      area('cred', 'cred', 'Credential', cCred),
      area('removed', 'removed', 'Removed', cRemoved),
      totalLine,
    ]
  }

  // grouped + weekly → side-by-side bars per category (weekly forces showValues).
  return [
    <Bar key="added" yAxisId="left" dataKey="added" name="Added" fill={cAdded}>
      {valueLabel('added')}
    </Bar>,
    <Bar key="changed" yAxisId="left" dataKey="changed" name="Changed" fill={cChanged}>
      {valueLabel('changed')}
    </Bar>,
    <Bar key="cred" yAxisId="left" dataKey="cred" name="Credential" fill={cCred}>
      {valueLabel('cred')}
    </Bar>,
    <Bar key="removed" yAxisId="left" dataKey="removed" name="Removed" fill={cRemoved}>
      {valueLabel('removed')}
    </Bar>,
    totalLine,
  ]
}

// ─────────────────────────────────────────────────────────────────────────────
// Calendar heatmap (custom; Recharts has no calendar mark)
// ─────────────────────────────────────────────────────────────────────────────

interface HeatmapProps {
  buckets: NormalizedBucket[]
  tokens: DashboardTokens
  showValues: boolean
  focusedIndex: number
  onDayClick?: (date: string) => void
}

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

interface CalCell {
  bucket: NormalizedBucket
  index: number
}

/**
 * Month-calendar grid. Weekday columns (Sun–Sat) span the full card width; each
 * week is a row. A day cell shows its date and a bottom **activity bar** whose
 * *length* encodes the day's volume (relative to the busiest day) and whose
 * coloured segments encode the category mix (added → changed → credential →
 * removed). Weekend columns are shaded. So a glance reads cadence ("which days /
 * weeks are busy") and character ("what kind of change") on a familiar calendar
 * — not a flat monochrome heat marooned in the corner.
 */
function Heatmap({ buckets, tokens, showValues, focusedIndex, onDayClick }: HeatmapProps) {
  const focusRef = useRef<HTMLButtonElement | null>(null)

  // Roving focus follows the keyboard cursor owned by the parent.
  useEffect(() => {
    focusRef.current?.focus()
  }, [focusedIndex])

  const maxTotal = useMemo(() => Math.max(1, ...buckets.map((b) => b.total)), [buckets])

  // Group days into week rows, aligned so each day sits under its weekday column.
  const weeks = useMemo<Array<Array<CalCell | null>>>(() => {
    if (!buckets.length) return []
    const firstWd = weekdayIndex(buckets[0].date)
    const firstSunday = new Date(`${buckets[0].date}T00:00:00Z`)
    firstSunday.setUTCDate(firstSunday.getUTCDate() - firstWd)

    const rows: Array<Array<CalCell | null>> = []
    buckets.forEach((bucket, index) => {
      const d = new Date(`${bucket.date}T00:00:00Z`)
      const dayDiff = Math.floor((d.getTime() - firstSunday.getTime()) / 86400000)
      const row = Math.floor(dayDiff / 7)
      const wd = d.getUTCDay()
      if (!rows[row]) rows[row] = Array(7).fill(null)
      rows[row][wd] = { bucket, index }
    })
    return rows
  }, [buckets])

  const cellBase = tokens.card2 || tokens.elevated || '#f1f3f9'
  const weekendBg = tokens.elevated || '#eef1f6'
  const catColors: Array<{ key: CatKey; field: 'added' | 'changed' | 'cred' | 'removed'; color: string }> = [
    { key: 'added', field: 'added', color: catColor(tokens, 'added') },
    { key: 'changed', field: 'changed', color: catColor(tokens, 'changed') },
    { key: 'cred', field: 'cred', color: catColor(tokens, 'cred') },
    { key: 'removed', field: 'removed', color: catColor(tokens, 'removed') },
  ]

  const gridCols = 'repeat(7, minmax(0, 1fr))'

  const dateLabel = (iso: string) => {
    const day = Number(iso.split('-')[2])
    return day === 1 ? shortDate(iso) : String(day) // month prefix on the 1st for context
  }

  return (
    <div style={{ padding: '4px 2px 2px' }}>
      {/* Weekday header */}
      <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 6, marginBottom: 6 }}>
        {WEEKDAY_LABELS.map((w, wd) => (
          <span
            key={w}
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: wd === 0 || wd === 6 ? tokens.muted : tokens.slate,
              textAlign: 'center',
            }}
          >
            {w}
          </span>
        ))}
      </div>

      {/* Week rows */}
      <div style={{ display: 'grid', gap: 6 }}>
        {weeks.map((week, wi) => (
          <div key={wi} style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 6 }}>
            {Array.from({ length: 7 }, (_, wd) => {
              const cell = week[wd] ?? null
              const isWeekendCol = wd === 0 || wd === 6
              if (!cell) {
                return (
                  <div
                    key={wd}
                    style={{
                      minHeight: 58,
                      borderRadius: 7,
                      background: isWeekendCol ? weekendBg : 'transparent',
                      opacity: 0.4,
                    }}
                  />
                )
              }
              const { bucket, index } = cell
              const isFocused = index === focusedIndex
              const isDefaultTab = focusedIndex < 0 && index === buckets.length - 1
              const fillPct = (bucket.total / maxTotal) * 100
              return (
                <button
                  key={wd}
                  ref={isFocused ? focusRef : undefined}
                  type="button"
                  tabIndex={isFocused || isDefaultTab ? 0 : -1}
                  aria-label={`${bucket.label}: ${bucket.total} changes — ${bucket.added} added, ${bucket.changed} changed, ${bucket.cred} credential, ${bucket.removed} removed`}
                  title={`${bucket.label} — ${bucket.total} changes (${bucket.added} added, ${bucket.changed} changed, ${bucket.cred} credential, ${bucket.removed} removed)`}
                  onClick={() => onDayClick?.(bucket.date)}
                  style={{
                    position: 'relative',
                    minHeight: 58,
                    borderRadius: 7,
                    background: isWeekendCol ? weekendBg : cellBase,
                    border: isFocused
                      ? `2px solid ${tokens.focus || '#4f46e5'}`
                      : `1px solid ${tokens.line || 'transparent'}`,
                    padding: '5px 7px 9px',
                    cursor: onDayClick ? 'pointer' : 'default',
                    textAlign: 'left',
                    display: 'block',
                  }}
                >
                  {/* Date + optional total */}
                  <span style={{ fontSize: 11, color: tokens.slate, fontWeight: 500 }}>{dateLabel(bucket.date)}</span>
                  {showValues && bucket.total ? (
                    <span style={{ float: 'right', fontSize: 11, fontWeight: 700, color: tokens.text }}>
                      {bucket.total}
                    </span>
                  ) : null}

                  {/* Activity bar — length = volume, segments = category mix */}
                  <div
                    style={{
                      position: 'absolute',
                      left: 7,
                      right: 7,
                      bottom: 6,
                      height: 9,
                      borderRadius: 5,
                      background: tokens.line || 'rgba(0,0,0,.08)',
                      overflow: 'hidden',
                    }}
                    aria-hidden
                  >
                    {bucket.total > 0 ? (
                      <div style={{ display: 'flex', height: '100%', width: `${Math.max(6, fillPct)}%` }}>
                        {catColors.map(({ key, field, color }) => {
                          const v = bucket[field]
                          if (!v) return null
                          return <span key={key} style={{ flexGrow: v, background: color }} />
                        })}
                      </div>
                    ) : null}
                  </div>
                </button>
              )
            })}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 12, paddingLeft: 2 }}>
        {catColors.map(({ key, color }) => (
          <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 11, height: 11, borderRadius: 2, background: color, display: 'inline-block' }} />
            <span style={{ fontSize: 12, color: tokens.slate }}>
              {CHANGE_CATEGORIES.find((c) => c.key === key)?.label}
            </span>
          </span>
        ))}
        <span style={{ fontSize: 12, color: tokens.muted }}>· bar length = daily volume</span>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// "View as table" — the 1.1.1 text alternative for every treatment
// ─────────────────────────────────────────────────────────────────────────────

interface ChangeTableProps {
  buckets: NormalizedBucket[]
  caption: string
  onDayClick?: (date: string) => void
}

function ChangeTable({ buckets, caption, onDayClick }: ChangeTableProps) {
  const th: CSSProperties = {
    textAlign: 'right',
    padding: '6px 10px',
    borderBottom: '2px solid var(--dv-line)',
    color: 'var(--dv-slate)',
    fontWeight: 600,
    fontSize: 13,
    whiteSpace: 'nowrap',
  }
  const td: CSSProperties = {
    textAlign: 'right',
    padding: '5px 10px',
    borderBottom: '1px solid var(--dv-line)',
    color: 'var(--dv-text)',
    fontSize: 13,
  }
  return (
    <div style={{ overflowX: 'auto', maxHeight: 360, overflowY: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 520 }}>
        <caption style={{ ...SR_ONLY }}>{caption}</caption>
        <thead>
          <tr>
            <th scope="col" style={{ ...th, textAlign: 'left' }}>
              Day
            </th>
            {CHANGE_CATEGORIES.map((c) => (
              <th key={c.key} scope="col" style={th}>
                {c.label}
              </th>
            ))}
            <th scope="col" style={th}>
              Total apps
            </th>
          </tr>
        </thead>
        <tbody>
          {buckets.map((b) => (
            <tr key={b.date}>
              <th scope="row" style={{ ...td, textAlign: 'left', fontWeight: 400 }}>
                {onDayClick ? (
                  <DefaultButton
                    text={b.label}
                    onClick={() => onDayClick(b.date)}
                    styles={{ root: { height: 24, padding: '0 6px', minWidth: 0, border: 'none', background: 'transparent' }, label: { fontWeight: 400 } } }
                  />
                ) : (
                  b.label
                )}
              </th>
              <td style={td}>{b.added}</td>
              <td style={td}>{b.changed}</td>
              <td style={td}>{b.cred}</td>
              <td style={td}>{b.removed}</td>
              <td style={{ ...td, fontWeight: 600 }}>{b.cumulativeTotal}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default LifecycleChart
